<template>
  <div class="app-questions list">

    <div class="page-header">
      <div class="page-header--title">
        <h1 class="title">Domaines</h1>
        <h2 class="subtitle">Gestion des domaines</h2>
      </div>
      <div class="page-header--actions">
        <b-button color="success" type="is-success" @click="create">
          <i class="fas fa-plus-circle"></i> Créer un domaine
        </b-button>
      </div>
    </div>

    <div class="page-content">
      <section class="list page-content-box">
        <b-table
          :data="domains"
          :loading="loading"
          hoverable
          striped
          paginated
        >

          <template slot="empty">
            <section class="section" v-if="!loading">
              <div class="content has-text-grey has-text-centered">
                <p>
                  <b-icon
                    icon="fas fa-frown"
                    size="is-large">
                  </b-icon>
                </p>
                <p>Aucun domaine.</p>
              </div>
            </section>
            <div v-if="loading">
              <b-skeleton animated></b-skeleton>
            </div>
          </template>

          <b-table-column field="label" label="Domaine" sortable searchable v-slot="props">
            {{ props.row.label }}
          </b-table-column>

          <b-table-column
            field="application.label"
            label="Application"
            sortable
            searchable
            :custom-search="applicationSearch"
          >
            <template #default="props">
              {{ getDomainApplicationLabel(props.row) }}
            </template>
            <template #searchable="{ column, filters }">
              <b-select size="is-small" v-model="filters[column.field]">
                <option value="">Toutes</option>
                <option
                  v-for="option in applicationOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </b-select>
            </template>
          </b-table-column>

          <b-table-column field="launch_date" label="Lancement" sortable v-slot="props">
            <t-datepicker
              class="domain-date-picker"
              :value="toDate(props.row.launch_date)"
              value-format="yyyy-MM-dd"
              user-format="d/m/Y"
              placeholder="jj/mm/aaaa"
              :week-start="1"
              clearable
              @input="updateDomainDate(props.row, 'launch_date', $event)"
            />
          </b-table-column>

          <b-table-column field="disabled_at" label="Désactivation" sortable v-slot="props">
            <t-datepicker
              class="domain-date-picker"
              :value="toDate(props.row.disabled_at)"
              value-format="yyyy-MM-dd"
              user-format="d/m/Y"
              placeholder="jj/mm/aaaa"
              :week-start="1"
              clearable
              :disabled="!isDomainDisabled(props.row)"
              @input="updateDomainDate(props.row, 'disabled_at', $event)"
            />
          </b-table-column>

          <b-table-column label="Actions" v-slot="props">
            <div class="buttons are-small">
              <b-button
                v-if="!isDomainDisabled(props.row)"
                size="is-small"
                type="is-warning"
                @click.stop="disableDomain(props.row)"
              >
                Désactiver
              </b-button>
              <b-button
                v-else
                size="is-small"
                type="is-success"
                @click.stop="enableDomain(props.row)"
              >
                Activer
              </b-button>
              <b-button
                size="is-small"
                type="is-danger"
                @click.stop="confirmDeleteDomain(props.row)"
              >
                Supprimer
              </b-button>
            </div>
          </b-table-column>

        </b-table>
      </section>
    </div>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import { mapGetters } from 'vuex'
import CreateDomain from './Modals/Create'

export default {
  name: 'app-domains-list',
  computed: {
    ...mapGetters([
      'roles',
      'application'
    ]),
    normalizedRoles () {
      const roles = this.roles || []
      if (!Array.isArray(roles)) {
        return []
      }
      return roles
        .map(role => {
          if (typeof role === 'string') {
            return role.toLowerCase()
          }
          if (role && typeof role.name === 'string') {
            return role.name.toLowerCase()
          }
          return ''
        })
        .filter(Boolean)
    },
    hasDomainManagementAccess () {
      return this.normalizedRoles.some(role => ['admin', 'community-manager'].includes(role))
    },
    currentApplicationSlug () {
      return this.application?.app?.slug || process.env.VUE_APP_APPLICATION
    },
    applicationOptions () {
      return [
        { value: 'maviepro', label: 'Maviepro' },
        { value: 'tract', label: 'Tract' },
        { value: 'disabled', label: 'Disabled' }
      ]
    }
  },
  data () {
    return {
      loading: false,
      isModalOpen: false,
      modalContent: {},
      hasInitialized: false,
      savingDomainDates: {},
      domains: [],
      emptyDomain: {
        faq: {},
        map: {},
        chat: {},
        news: {},
        payment: {},
        stat: {},
        survey: {}
      }
    }
  },
  created () {
    this.ensureDomainManagementAccess()
    this.initIfAllowed()
  },
  watch: {
    normalizedRoles () {
      this.ensureDomainManagementAccess()
      this.initIfAllowed()
    }
  },
  methods: {
    ensureDomainManagementAccess () {
      if (!this.normalizedRoles.length) {
        return
      }
      if (this.hasDomainManagementAccess) {
        return
      }
      this.$buefy.notification.open({
        duration: 3500,
        message: 'Vous n’avez pas accès à la gestion des domaines.',
        position: 'is-bottom-left',
        type: 'is-danger',
        hasIcon: true
      })
      this.$router.replace({ path: '/app/dashboard' })
    },
    initIfAllowed () {
      if (!this.hasDomainManagementAccess || this.hasInitialized) {
        return
      }
      this.hasInitialized = true
      this.loadData()
    },
    loadData () {
      this.loading = true
      http.get('/domain').then(({ data }) => {
        this.loading = false
        this.domains = data
      }).catch((err) => {
        console.log(err)
        this.loading = false
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Nos serveurs rencontrent des difficultés à récuperer la liste des domaines',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    isDomainDisabled (domain) {
      return domain?.application?.slug === 'disabled'
    },
    getDomainApplicationSlug (domain) {
      return domain?.application?.slug || 'disabled'
    },
    getDomainApplicationLabel (domain) {
      const slug = this.getDomainApplicationSlug(domain)
      const option = this.applicationOptions.find(item => item.value === slug)
      return domain?.application?.label || option?.label || slug
    },
    applicationSearch (row, filter) {
      if (!filter) {
        return true
      }
      return this.getDomainApplicationSlug(row) === filter
    },
    updateDomainRow (updatedDomain) {
      const index = this.domains.findIndex(domain => domain.uuid === updatedDomain.uuid)
      if (index === -1) {
        return
      }
      this.$set(this.domains, index, {
        ...this.domains[index],
        ...updatedDomain
      })
    },
    updateDomainDate (domain, field, value) {
      if (!domain || !domain.uuid || !['launch_date', 'disabled_at'].includes(field)) {
        return
      }

      const nextValue = this.normalizeDate(value)
      const previousValue = this.normalizeDate(domain[field])

      if (nextValue === previousValue || this.savingDomainDates[`${domain.uuid}:${field}`]) {
        return
      }

      const key = `${domain.uuid}:${field}`
      this.$set(this.savingDomainDates, key, true)
      this.$set(domain, field, nextValue)

      http.put(`/domain/${domain.uuid}`, { [field]: nextValue }).then(({ data }) => {
        this.updateDomainRow(data)
        this.$buefy.notification.open({
          duration: 2200,
          message: `La date du domaine <strong>${domain.label}</strong> a été mise à jour.`,
          position: 'is-bottom-left',
          type: 'is-success',
          hasIcon: true
        })
      }).catch((err) => {
        console.log(err)
        this.$set(domain, field, previousValue)
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour cette date',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      }).finally(() => {
        this.$delete(this.savingDomainDates, key)
      })
    },
    normalizeDate (value) {
      if (!value) {
        return null
      }

      if (value instanceof Date) {
        return this.toIsoDate(value)
      }

      if (typeof value === 'string') {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) {
          const [, year, month, day] = isoMatch
          return this.toIsoDate(new Date(Number(year), Number(month) - 1, Number(day)))
        }

        const frenchMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        if (frenchMatch) {
          const [, day, month, year] = frenchMatch
          return this.toIsoDate(new Date(Number(year), Number(month) - 1, Number(day)))
        }

        return this.toIsoDate(new Date(value))
      }

      return null
    },
    toIsoDate (date) {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    toDate (value) {
      if (!value) {
        return null
      }

      if (value instanceof Date) {
        return value
      }

      if (typeof value === 'string') {
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
        if (isoMatch) {
          const [, year, month, day] = isoMatch
          const date = new Date(Number(year), Number(month) - 1, Number(day))
          return isNaN(date.getTime()) ? null : date
        }

        const frenchMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
        if (frenchMatch) {
          const [, day, month, year] = frenchMatch
          const date = new Date(Number(year), Number(month) - 1, Number(day))
          return isNaN(date.getTime()) ? null : date
        }
      }

      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? null : parsed
    },
    setDomainApplication (domain, slug) {
      const currentSlug = this.getDomainApplicationSlug(domain)
      if (slug === currentSlug) {
        return
      }
      this.loading = true
      http.post(`/domain/${domain.uuid}/application`, { slug }).then(({ data }) => {
        this.loading = false
        this.updateDomainRow(data)
        this.$buefy.notification.open({
          duration: 3000,
          message: `Le domaine <strong>${domain.label}</strong> a été mis à jour.`,
          position: 'is-bottom-left',
          type: slug === 'disabled' ? 'is-warning' : 'is-success',
          hasIcon: true
        })
      }).catch((err) => {
        console.log(err)
        this.loading = false
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Nos serveurs rencontrent des difficultés à mettre à jour ce domaine',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      })
    },
    disableDomain (domain) {
      this.$buefy.dialog.confirm({
        title: 'Désactivation',
        message: `Désactiver le domaine <strong>${domain.label}</strong> ?`,
        cancelText: 'Annuler',
        confirmText: 'Désactiver',
        type: 'is-warning',
        onConfirm: () => {
          this.setDomainApplication(domain, 'disabled')
        }
      })
    },
    enableDomain (domain) {
      if (!this.currentApplicationSlug) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Application courante introuvable pour réactiver ce domaine.',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
        return
      }
      this.$buefy.dialog.confirm({
        title: 'Activation',
        message: `Activer le domaine <strong>${domain.label}</strong> ?`,
        cancelText: 'Annuler',
        confirmText: 'Activer',
        type: 'is-success',
        onConfirm: () => {
          this.setDomainApplication(domain, this.currentApplicationSlug)
        }
      })
    },
    confirmDeleteDomain (domain) {
      const expected = domain.slug || domain.label
      this.$buefy.dialog.prompt({
        title: 'Suppression définitive',
        message: `Cette action supprimera définitivement le domaine <strong>${domain.label}</strong> et toutes ses données associées.<br><br>Veuillez saisir <strong>${expected}</strong> pour confirmer.`,
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        type: 'is-danger',
        inputAttrs: {
          placeholder: expected
        },
        onConfirm: value => {
          const normalizedValue = String(value || '').trim()
          if (normalizedValue !== expected) {
            this.$buefy.toast.open({
              message: 'Confirmation incorrecte. Suppression annulée.',
              type: 'is-danger',
              position: 'is-bottom-left',
              duration: 4000
            })
            return
          }
          this.loading = true
          http.delete(`/domain/${domain.uuid}`).then(() => {
            this.loading = false
            this.domains = this.domains.filter(item => item.uuid !== domain.uuid)
            this.$buefy.notification.open({
              duration: 3000,
              message: `Le domaine <strong>${domain.label}</strong> a été supprimé.`,
              position: 'is-bottom-left',
              type: 'is-success',
              hasIcon: true
            })
          }).catch((err) => {
            console.log(err)
            this.loading = false
            this.$buefy.notification.open({
              duration: 3000,
              message: 'Nos serveurs rencontrent des difficultés à supprimer ce domaine',
              position: 'is-bottom-left',
              type: 'is-warning',
              hasIcon: true,
              icon: 'server'
            })
          })
        }
      })
    },

    // Open Modal
    create () {
      this.$buefy.modal.open({
        parent: this,
        component: CreateDomain,
        hasModalCard: true,
        width: 800,
        destroyOnHide: false,
        ariaRole: 'dialog',
        ariaModal: true,
        events: {
          created: (created) => {
            this.domains.push(created)
            this.$buefy.notification.open({
              duration: 4000,
              message: 'Le domaine a été créée avec succès !',
              position: 'is-bottom-left',
              type: 'is-success',
              hasIcon: true
            })
          }
        }
      })
    }

    // Table Drag / Drop disabled
  }
}
</script>

<style lang="scss">

</style>
